import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaSync, FaUpload } from 'react-icons/fa';
import { toast } from 'react-hot-toast';

// Stable style objects to prevent re-render loops
const modalBackdropStyle = { backgroundColor: 'rgba(0,0,0,0.5)' };
const modalHeaderStyle = { backgroundColor: '#6B8E23', color: 'white' };

// Comprehensive list of countries
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
  'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon',
  'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos',
  'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi',
  'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova',
  'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands',
  'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau',
  'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal',
  'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea',
  'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan',
  'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
  'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'admin',
    password: '',
    is_active: true,
    // Country Director specific fields
    professional_title: '',
    profile_picture_url: '',
    country: '',
    city_region: '',
    phone_number: '',
    linkedin_profile: '',
    role_description: '',
    professional_background: '',
    certifications: '',
    appointment_date: '',
    department_unit: '',
    reporting_structure: '',
    short_bio: '',
    languages_spoken: '',
    areas_of_expertise: '',
    achievements_publications: '',
    emergency_contact: '',
    email_signature: ''
  });
  const [activeTab, setActiveTab] = useState('basic'); // For tabbed interface
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Fetch users only once on mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;

      if (!API_BASE) {
        throw new Error('REACT_APP_API_URL not configured');
      }

      const response = await fetch(`${API_BASE}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();

      if (data.success) {
        setUsers(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Fetch users error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const API_BASE = process.env.REACT_APP_API_URL;
  if (!API_BASE) {
    throw new Error('API URL not configured');
  }

  const handleCreate = () => {
    setModalMode('create');
    setSelectedUser(null);
    setActiveTab('basic');
    setProfilePictureFile(null);
    setShowPassword(false);
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      role: 'admin',
      password: '',
      is_active: true,
      professional_title: '',
      profile_picture_url: '',
      country: '',
      city_region: '',
      phone_number: '',
      linkedin_profile: '',
      role_description: '',
      professional_background: '',
      certifications: '',
      appointment_date: '',
      department_unit: '',
      reporting_structure: '',
      short_bio: '',
      languages_spoken: '',
      areas_of_expertise: '',
      achievements_publications: '',
      emergency_contact: '',
      email_signature: ''
    });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setActiveTab('basic');
    setProfilePictureFile(null);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      role: user.role || 'admin',
      password: '',
      is_active: user.is_active !== false,
      professional_title: user.professional_title || '',
      profile_picture_url: user.profile_picture_url || '',
      country: user.country || '',
      city_region: user.city_region || '',
      phone_number: user.phone_number || '',
      linkedin_profile: user.linkedin_profile || '',
      role_description: user.role_description || '',
      professional_background: user.professional_background || '',
      certifications: user.certifications || '',
      appointment_date: user.appointment_date || '',
      department_unit: user.department_unit || '',
      reporting_structure: user.reporting_structure || '',
      short_bio: user.short_bio || '',
      languages_spoken: user.languages_spoken || '',
      areas_of_expertise: user.areas_of_expertise || '',
      achievements_publications: user.achievements_publications || '',
      emergency_contact: user.emergency_contact || '',
      email_signature: user.email_signature || ''
    });
    setShowModal(true);
  };

  const handleView = (user) => {
    setModalMode('view');
    setSelectedUser(user);
    setActiveTab('basic');
    setFormData({
      username: user.username || '',
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      role: user.role || 'admin',
      password: '',
      is_active: user.is_active !== false,
      professional_title: user.professional_title || '',
      profile_picture_url: user.profile_picture_url || '',
      country: user.country || '',
      city_region: user.city_region || '',
      phone_number: user.phone_number || '',
      linkedin_profile: user.linkedin_profile || '',
      role_description: user.role_description || '',
      professional_background: user.professional_background || '',
      certifications: user.certifications || '',
      appointment_date: user.appointment_date ? user.appointment_date.split('T')[0] : '',
      department_unit: user.department_unit || '',
      reporting_structure: user.reporting_structure || '',
      short_bio: user.short_bio || '',
      languages_spoken: user.languages_spoken || '',
      areas_of_expertise: user.areas_of_expertise || '',
      achievements_publications: user.achievements_publications || '',
      emergency_contact: user.emergency_contact || '',
      email_signature: user.email_signature || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user ${user.username}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('REACT_APP_API_URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete user: ' + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('adminToken');

      // Upload profile picture first if provided
      let profilePictureUrl = formData.profile_picture_url;
      if (profilePictureFile) {
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('image', profilePictureFile);

          const uploadResponse = await fetch(`${API_BASE}/admin-upload/leadership/image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: uploadFormData
          });

          if (uploadResponse.ok) {
            const uploadData = await uploadResponse.json();
            profilePictureUrl = uploadData.imageUrl;
            toast.success('Profile picture uploaded successfully');
          } else {
            throw new Error('Failed to upload profile picture');
          }
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          toast.error('Failed to upload profile picture');
          return; // Don't proceed if image upload fails
        }
      }

      const userData = {
        username: formData.username,
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        is_active: formData.is_active
      };

      // Add password only if provided
      if (formData.password) {
        userData.password = formData.password;
      }

      // Add Country Director specific fields if role is country_director
      if (formData.role === 'country_director') {
        userData.professional_title = formData.professional_title;
        userData.profile_picture_url = profilePictureUrl; // Use uploaded URL
        userData.country = formData.country;
        userData.city_region = formData.city_region;
        userData.phone_number = formData.phone_number;
        userData.linkedin_profile = formData.linkedin_profile;
        userData.role_description = formData.role_description;
        userData.professional_background = formData.professional_background;
        userData.certifications = formData.certifications;
        userData.appointment_date = formData.appointment_date;
        userData.department_unit = formData.department_unit;
        userData.reporting_structure = formData.reporting_structure;
        userData.short_bio = formData.short_bio;
        userData.languages_spoken = formData.languages_spoken;
        userData.areas_of_expertise = formData.areas_of_expertise;
        userData.achievements_publications = formData.achievements_publications;
        userData.emergency_contact = formData.emergency_contact;
        userData.email_signature = formData.email_signature;
      }

      let response;
      if (modalMode === 'create') {
        response = await fetch(`${API_BASE}/admin/users`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(userData)
        });
      } else {
        response = await fetch(`${API_BASE}/admin/users/${selectedUser.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(userData)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Operation failed');
      }

      toast.success(`User ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
      setShowModal(false);
      loadUsers();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to save user: ' + error.message);
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-danger';
      case 'admin': return 'bg-success';
      case 'country_director': return 'bg-primary';
      case 'moderator': return 'bg-info';
      default: return 'bg-secondary';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'country_director': return 'Country Director';
      case 'moderator': return 'Moderator';
      default: return role;
    }
  };

  if (loading) {
    return (
      <div className="container-fluid py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card shadow">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h3 className="h5 mb-0">User Management</h3>
          <div>
            <button className="btn btn-primary btn-sm me-2" onClick={handleCreate}>
              <FaPlus className="me-1" /> Add User
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={loadUsers}>
              <FaSync className="me-1" /> Refresh
            </button>
          </div>
        </div>

        <div className="card-body">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>{`${user.first_name || ''} ${user.last_name || ''}`.trim() || '-'}</td>
                      <td>
                        <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${user.is_active ? 'bg-success' : 'bg-secondary'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{user.last_login_at ? new Date(user.last_login_at).toLocaleString() : '-'}</td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleView(user)}
                            title="View"
                          >
                            <FaEye />
                          </button>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => handleEdit(user)}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(user)}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <div className="text-muted">No users found</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={modalBackdropStyle}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={modalHeaderStyle}>
                <h5 className="modal-title">
                  {modalMode === 'create' ? 'Create New User' : modalMode === 'edit' ? 'Edit User' : 'View User'}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>

              {modalMode === 'view' ? (
                <div className="modal-body">
                  <div className="mb-3">
                    <strong>Username:</strong> {selectedUser?.username}
                  </div>
                  <div className="mb-3">
                    <strong>Email:</strong> {selectedUser?.email}
                  </div>
                  <div className="mb-3">
                    <strong>First Name:</strong> {selectedUser?.first_name || '-'}
                  </div>
                  <div className="mb-3">
                    <strong>Last Name:</strong> {selectedUser?.last_name || '-'}
                  </div>
                  <div className="mb-3">
                    <strong>Role:</strong>{' '}
                    <span className={`badge ${getRoleBadgeClass(selectedUser?.role)}`}>
                      {getRoleLabel(selectedUser?.role)}
                    </span>
                  </div>
                  <div className="mb-3">
                    <strong>Status:</strong>{' '}
                    <span className={`badge ${selectedUser?.is_active ? 'bg-success' : 'bg-secondary'}`}>
                      {selectedUser?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="mb-3">
                    <strong>Created:</strong> {selectedUser?.created_at ? new Date(selectedUser.created_at).toLocaleString() : '-'}
                  </div>
                  <div className="mb-3">
                    <strong>Last Login:</strong> {selectedUser?.last_login_at ? new Date(selectedUser.last_login_at).toLocaleString() : '-'}
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {/* Role Selection - Always First */}
                    <div className="mb-4 p-3 bg-light border rounded">
                      <label className="form-label fw-bold">User Type *</label>
                      <select
                        className="form-select"
                        value={formData.role}
                        onChange={(e) => {
                          setFormData({ ...formData, role: e.target.value });
                          // Auto-format professional title for Country Director
                          if (e.target.value === 'country_director' && formData.country) {
                            setFormData(prev => ({
                              ...prev,
                              role: e.target.value,
                              professional_title: `Country Director, ARCA – ${formData.country}`
                            }));
                          }
                        }}
                        required
                      >
                        <option value="admin">Admin</option>
                        <option value="country_director">Country Director</option>
                        <option value="super_admin">Super Admin</option>
                        <option value="moderator">Moderator</option>
                      </select>
                      <small className="text-muted">
                        {formData.role === 'country_director' && 'Country Directors manage regional operations'}
                        {formData.role === 'admin' && 'Admins have full system access'}
                        {formData.role === 'super_admin' && 'Super Admins have unrestricted access'}
                      </small>
                    </div>

                    {/* Basic Information - For All Users */}
                    <div className="border-bottom pb-3 mb-3">
                      <h6 className="fw-bold text-primary mb-3">Basic Information</h6>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Username *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            required
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">Email *</label>
                          <input
                            type="email"
                            className="form-control"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                          />
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">First Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            required
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">Last Name *</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            required
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          Password {modalMode === 'edit' && '(leave blank to keep current)'}
                        </label>
                        <div className="input-group">
                          <input
                            type={showPassword ? "text" : "password"}
                            className="form-control"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            required={modalMode === 'create'}
                          />
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            title={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          />
                          <label className="form-check-label">Active</label>
                        </div>
                      </div>
                    </div>

                    {/* Country Director Specific Fields */}
                    {formData.role === 'country_director' && (
                      <>
                        {/* Professional Details */}
                        <div className="border-bottom pb-3 mb-3">
                          <h6 className="fw-bold text-primary mb-3">Professional Details</h6>

                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Country *</label>
                              <select
                                className="form-select"
                                value={formData.country}
                                onChange={(e) => {
                                  const country = e.target.value;
                                  setFormData({
                                    ...formData,
                                    country,
                                    professional_title: country ? `Country Director, ARCA – ${country}` : ''
                                  });
                                }}
                                required
                              >
                                <option value="">Select Country</option>
                                {COUNTRIES.map(country => (
                                  <option key={country} value={country}>{country}</option>
                                ))}
                              </select>
                            </div>

                            <div className="col-md-6 mb-3">
                              <label className="form-label">City/Region</label>
                              <input
                                type="text"
                                className="form-control"
                                value={formData.city_region}
                                onChange={(e) => setFormData({ ...formData, city_region: e.target.value })}
                                placeholder="e.g., Accra, Lagos"
                              />
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Professional Title *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.professional_title}
                              onChange={(e) => setFormData({ ...formData, professional_title: e.target.value })}
                              placeholder="Country Director, ARCA – [Country]"
                              required
                            />
                            <small className="text-muted">Auto-filled based on country selection</small>
                          </div>

                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Phone Number *</label>
                              <input
                                type="tel"
                                className="form-control"
                                value={formData.phone_number}
                                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                                placeholder="+233 000 000 000"
                                required
                              />
                            </div>

                            <div className="col-md-6 mb-3">
                              <label className="form-label">LinkedIn Profile</label>
                              <input
                                type="url"
                                className="form-control"
                                value={formData.linkedin_profile}
                                onChange={(e) => setFormData({ ...formData, linkedin_profile: e.target.value })}
                                placeholder="https://linkedin.com/in/..."
                              />
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Profile Picture</label>
                            <input
                              type="file"
                              className="form-control"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  // Validate file size (max 5MB)
                                  if (file.size > 5 * 1024 * 1024) {
                                    toast.error('File size must be less than 5MB');
                                    e.target.value = '';
                                    return;
                                  }
                                  // Validate file type
                                  if (!file.type.startsWith('image/')) {
                                    toast.error('Only image files are allowed');
                                    e.target.value = '';
                                    return;
                                  }
                                  setProfilePictureFile(file);
                                }
                              }}
                            />
                            <small className="text-muted">
                              Professional headshot recommended. Max size: 5MB. Formats: JPG, PNG, GIF
                            </small>
                            {profilePictureFile && (
                              <div className="mt-2">
                                <span className="badge bg-success">
                                  <FaUpload className="me-1" />
                                  {profilePictureFile.name}
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-danger"
                                  onClick={() => setProfilePictureFile(null)}
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Appointment Date *</label>
                            <input
                              type="date"
                              className="form-control"
                              value={formData.appointment_date}
                              onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                              required
                            />
                          </div>
                        </div>

                        {/* Role & Responsibilities */}
                        <div className="border-bottom pb-3 mb-3">
                          <h6 className="fw-bold text-primary mb-3">Role & Responsibilities</h6>

                          <div className="mb-3">
                            <label className="form-label">Role Description *</label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={formData.role_description}
                              onChange={(e) => setFormData({ ...formData, role_description: e.target.value })}
                              placeholder="Describe the role and key responsibilities..."
                              required
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Department/Unit</label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.department_unit}
                              onChange={(e) => setFormData({ ...formData, department_unit: e.target.value })}
                              placeholder="e.g., West Africa Region"
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Reporting Structure *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.reporting_structure}
                              onChange={(e) => setFormData({ ...formData, reporting_structure: e.target.value })}
                              placeholder="e.g., Reports to ARCA President"
                              required
                            />
                          </div>
                        </div>

                        {/* Professional Background */}
                        <div className="border-bottom pb-3 mb-3">
                          <h6 className="fw-bold text-primary mb-3">Professional Background</h6>

                          <div className="mb-3">
                            <label className="form-label">Professional Background *</label>
                            <textarea
                              className="form-control"
                              rows="4"
                              value={formData.professional_background}
                              onChange={(e) => setFormData({ ...formData, professional_background: e.target.value })}
                              placeholder="Describe your professional experience, education, and career highlights..."
                              required
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Short Bio *</label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={formData.short_bio}
                              onChange={(e) => setFormData({ ...formData, short_bio: e.target.value })}
                              placeholder="A brief professional bio for public display..."
                              required
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Certifications/Credentials *</label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={formData.certifications}
                              onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                              placeholder="List relevant certifications, licenses, and credentials..."
                              required
                            />
                          </div>
                        </div>

                        {/* Expertise & Achievements */}
                        <div className="border-bottom pb-3 mb-3">
                          <h6 className="fw-bold text-primary mb-3">Expertise & Achievements</h6>

                          <div className="mb-3">
                            <label className="form-label">Areas of Expertise *</label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={formData.areas_of_expertise}
                              onChange={(e) => setFormData({ ...formData, areas_of_expertise: e.target.value })}
                              placeholder="e.g., Pediatric respiratory care, ICU management, Training & Education..."
                              required
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Achievements/Publications</label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={formData.achievements_publications}
                              onChange={(e) => setFormData({ ...formData, achievements_publications: e.target.value })}
                              placeholder="Notable achievements, publications, awards, and recognitions..."
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Languages Spoken *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.languages_spoken}
                              onChange={(e) => setFormData({ ...formData, languages_spoken: e.target.value })}
                              placeholder="e.g., English, French, Swahili"
                              required
                            />
                          </div>
                        </div>

                        {/* Additional Information */}
                        <div className="pb-3 mb-3">
                          <h6 className="fw-bold text-primary mb-3">Additional Information</h6>

                          <div className="mb-3">
                            <label className="form-label">Emergency/Alternate Contact</label>
                            <textarea
                              className="form-control"
                              rows="2"
                              value={formData.emergency_contact}
                              onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                              placeholder='Name, Relationship, Phone (e.g., "John Doe, Spouse, +233 000 000 001")'
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Email Signature Block</label>
                            <textarea
                              className="form-control"
                              rows="4"
                              value={formData.email_signature}
                              onChange={(e) => setFormData({ ...formData, email_signature: e.target.value })}
                              placeholder="Professional email signature..."
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Admin Specific Fields (fewer than Country Director) */}
                    {(formData.role === 'admin' || formData.role === 'super_admin') && (
                      <div className="border-bottom pb-3 mb-3">
                        <h6 className="fw-bold text-primary mb-3">Additional Details</h6>

                        <div className="mb-3">
                          <label className="form-label">Phone Number</label>
                          <input
                            type="tel"
                            className="form-control"
                            value={formData.phone_number}
                            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                            placeholder="+233 000 000 000"
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Department/Unit</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.department_unit}
                            onChange={(e) => setFormData({ ...formData, department_unit: e.target.value })}
                            placeholder="e.g., Administration, IT"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn text-white" style={{ backgroundColor: '#6B8E23' }}>
                      {modalMode === 'create' ? 'Create User' : 'Update User'}
                    </button>
                  </div>
                </form>
              )}

              {modalMode === 'view' && (
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
